/**
 * Convite para a comunidade: Discord e WhatsApp.
 *
 * Cada porta é um bloco com a cor da própria marca, o ícone gigante vazando
 * pelo canto e um botão cheio -- não um cartão cinza com link. O Discord mostra
 * membros e quem está online agora, lidos da API pública do convite; se ela
 * não responder, os números simplesmente não aparecem (nada de estimativa).
 */

import { useQuery } from '@tanstack/react-query';

const CONVITE_DISCORD = 'mywHP8Rww';
const DISCORD = `https://discord.gg/${CONVITE_DISCORD}`;
const WHATSAPP = 'https://chat.whatsapp.com/DMg35ReAcWD3lEzZmWQhNQ';

function IconeDiscord({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

function IconeWhatsapp({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  );
}

interface ConviteDiscord {
  approximate_member_count?: number;
  approximate_presence_count?: number;
}

function useDiscord() {
  return useQuery({
    queryKey: ['discord-convite', CONVITE_DISCORD],
    queryFn: async () => {
      const r = await fetch(`https://discord.com/api/v9/invites/${CONVITE_DISCORD}?with_counts=true`);
      if (!r.ok) throw new Error('convite indisponível');
      return (await r.json()) as ConviteDiscord;
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
}

const n = (v: number) => v.toLocaleString('pt-BR');

export function Comunidade() {
  const discord = useDiscord().data;
  const membros = discord?.approximate_member_count;
  const online = discord?.approximate_presence_count;

  return (
    <section className="relative z-10 overflow-hidden border-t border-line py-24" aria-labelledby="comunidade-titulo">
      <div className="mx-auto max-w-ucc px-6">
        <h2 id="comunidade-titulo" className="ucc-display mb-12 max-w-4xl text-[clamp(2.75rem,7vw,5.5rem)] text-paper">
          A resenha não acaba no <span className="text-gold-ink">apito final.</span>
        </h2>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          {/* Discord */}
          <a
            href={DISCORD}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex min-h-[22rem] flex-col justify-between overflow-hidden rounded-2xl bg-[linear-gradient(140deg,#5865F2,#3c45a5_70%)] p-8 text-white sm:p-10"
          >
            <IconeDiscord className="pointer-events-none absolute -right-10 -bottom-12 size-80 text-white/10 transition-transform duration-[--duration-hero] ease-[--ease-signature] group-hover:-rotate-12 group-hover:scale-110" />
            <div className="relative flex items-center gap-3">
              <IconeDiscord className="size-9" />
              <span className="text-sm font-bold tracking-wider uppercase">Servidor oficial no Discord</span>
            </div>
            <div className="relative">
              {membros ? (
                <p className="flex flex-wrap items-end gap-x-6 gap-y-2">
                  <span>
                    <span className="ucc-display tabular block text-[clamp(4rem,9vw,6rem)] leading-none">{n(membros)}</span>
                    <span className="text-sm font-semibold text-white/75">membros</span>
                  </span>
                  {online ? (
                    <span className="mb-1 inline-flex items-center gap-2 rounded-full bg-black/25 px-3 py-1.5 text-sm font-semibold">
                      <span className="relative flex size-2.5">
                        <span className="absolute inset-0 animate-ping rounded-full bg-[#3ba55d] opacity-75 motion-reduce:hidden" />
                        <span className="relative size-2.5 rounded-full bg-[#3ba55d]" />
                      </span>
                      <span className="tabular">{n(online)}</span> online agora
                    </span>
                  ) : null}
                </p>
              ) : (
                <p className="ucc-display text-[clamp(3rem,7vw,5rem)] leading-none">Discord</p>
              )}
              <p className="mt-4 max-w-md text-base text-white/80">Converse com a organização e com os outros clubes do campeonato.</p>
              <span className="mt-7 inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3.5 text-sm font-bold tracking-wider text-[#3c45a5] uppercase transition-transform duration-[--duration-quick] group-hover:translate-x-1">
                Entrar no servidor
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="size-4" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </span>
            </div>
          </a>

          {/* WhatsApp */}
          <a
            href={WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex min-h-[22rem] flex-col justify-between overflow-hidden rounded-2xl bg-[linear-gradient(140deg,#1faa55,#0f6e38_75%)] p-8 text-white sm:p-10"
          >
            <IconeWhatsapp className="pointer-events-none absolute -right-12 -bottom-14 size-80 text-white/10 transition-transform duration-[--duration-hero] ease-[--ease-signature] group-hover:rotate-12 group-hover:scale-110" />
            <div className="relative flex items-center gap-3">
              <IconeWhatsapp className="size-9" />
              <span className="text-sm font-bold tracking-wider uppercase">Comunidade no WhatsApp</span>
            </div>
            <div className="relative">
              <p className="ucc-display text-[clamp(3rem,7vw,5rem)] leading-none">Avisos no celular</p>
              <p className="mt-4 max-w-md text-base text-white/80">Receba os avisos da organização direto no WhatsApp.</p>
              <span className="mt-7 inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3.5 text-sm font-bold tracking-wider text-[#0f6e38] uppercase transition-transform duration-[--duration-quick] group-hover:translate-x-1">
                Entrar no grupo
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="size-4" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </span>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
